import { gql } from "@apollo/client";
import { t } from "i18next";
// ========== GraphQL Fragments ==========

export const CONSOLIDATION_TASK_SUMMARY_FIELDS = gql`
    fragment ConsolidationTaskSummaryFields on ConsolidationTaskSummaryType {
        taskId
        taskName
        hubName
        whsName
        createdAt
        parcelCount
        bagCount
        status
        isEditable
        isDeletable
        carrier
        handoverAt
        handoverVehicles
    }
`;

export const CONSOLIDATION_TASK_INFO_FIELDS = gql`
    fragment ConsolidationTaskInfoFields on ConsolidationTaskInfoType {
        taskId
        taskName
        destinationHubId
        destinationHubName
        destinationWhsId
        destinationWhsName
        startTime
        endTime
        bagType
        status
        createdAt
        carrier
        carrierCode
    }
`;

export const CONSOLIDATION_BAG_DETAIL_FIELDS = gql`
    fragment ConsolidationBagDetailFields on ConsolidationBagDetailType {
        bagId
        bagNumber
        bagCode
        parcelCount
        handoverVehicle
        handoverAt
    }
`;

export const ROUTE_STATISTICS_FIELDS = gql`
    fragment RouteStatisticsFields on RouteStatisticsType {
        routeCode
        parcelCount
        estimatedBagCount
    }
`;

// ========== TypeScript Types ==========

export interface ConsolidationTaskSummary {
    taskId: string;
    taskName: string;
    hubName?: string;
    whsName?: string;
    createdAt: string;
    parcelCount: number;
    bagCount: number;
    status: ConsolidationTaskStatus;
    isEditable: boolean;
    isDeletable: boolean;
    carrier?: string; // Carrier文本值
    handoverAt?: string; // Handover时间
    handoverVehicles?: string; // Handover车辆
}

export interface ConsolidationTaskInfo {
    taskId: string;
    taskName: string;
    destinationHubId?: string;
    destinationHubName?: string;
    destinationWhsId?: string;
    destinationWhsName?: string;
    startTime?: string;
    endTime?: string;
    bagType?: BagType;
    status: ConsolidationTaskStatus;
    createdAt: string;
    carrier?: string;
    carrierCode?: number;
}

export interface ConsolidationBagDetail {
    bagId: string;
    bagNumber: string;
    bagCode: string;
    parcelCount: number;
    handoverVehicle?: string;
    handoverAt?: string;
}

export interface ConsolidationTaskStatistics {
    totalBagCount: number;
    totalParcelCount: number;
    shippedBagCount: number;
    routeBreakdown: RouteStatistics[];
}

export interface RouteStatistics {
    routeCode: string;
    parcelCount: number;
    estimatedBagCount: number;
}

export interface ConsolidationTotalStats {
    totalTaskCount: number;
    activeTaskCount: number;
    totalParcelCount: number;
    totalBagCount: number;
}

export interface Pagination {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
}

export interface HubOption {
    hubId: string;
    hubName: string;
    sortingCode?: string;
}

export interface WhsOption {
    whsId: string;
    whsName: string;
    parentHubId?: string;
    sortingCode?: string;
}

export interface ConsolidationTaskOption {
    taskId: string;
    displayName: string;
    hubName?: string;
    whsName?: string;
    available: boolean;
}

export interface ConsolidationOrderDetail {
    orderId: string;
    trackingNumber: string;
    sortingCode: string;
    expectedDeliveryTime?: string;
    weight?: number;
    dimensions?: string;
    senderAddress?: string;
    recipientAddress?: string;
}

export interface ConsolidationRouteOrders {
    routeCode: string;
    orders: ConsolidationOrderDetail[];
}

export interface NewBagRequirement {
    taskId: string;
    routeCode: string;
    trackingNumbers: string[];
    estimatedParcelCount: number;
}

export interface FailedOrder {
    trackingNumber: string;
    errorMessage: string;
}

export interface CreatedBagInfo {
    bagId: string;
    bagNumber: string;
    bagCode: string;
    actualParcelCount: number;
}

// ========== Enums ==========

export enum ConsolidationTaskStatus {
    CREATED = 'CREATED',
    DISSOLVED = 'DISSOLVED',
    COMPLETED = 'COMPLETED',
    HANDOVER = 'HANDOVER'
}

export enum BagType {
    ONE_SEGMENT = 'ONE_SEGMENT',
    TWO_SEGMENT = 'TWO_SEGMENT',
    THREE_SEGMENT = 'THREE_SEGMENT'
}

export enum BagTaskLogOperateStatus {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED'
}

// ========== Helper Functions ==========

export const getStatusText = (status: ConsolidationTaskStatus): string => {
    switch (status) {
        case ConsolidationTaskStatus.CREATED:
            return t('wms.baggingTask.status.created');
        case ConsolidationTaskStatus.DISSOLVED:
            return t('wms.baggingTask.status.discarded');
        case ConsolidationTaskStatus.COMPLETED:
            return t('wms.baggingTask.status.completed');
        case ConsolidationTaskStatus.HANDOVER:
            return t('wms.baggingTask.status.handover');
        default:
            return t('wms.baggingTask.status.unknown');
    }
};

export const getStatusColor = (status: ConsolidationTaskStatus): string => {
    switch (status) {
        case ConsolidationTaskStatus.CREATED:
            return 'blue';
        case ConsolidationTaskStatus.DISSOLVED:
            return 'grey';
        case ConsolidationTaskStatus.COMPLETED:
            return 'green';
        case ConsolidationTaskStatus.HANDOVER:
            return 'orange';
        default:
            return 'default';
    }
};

export const getBagTypeOptions = () => [
    { value: BagType.ONE_SEGMENT, label: 'ONE_SEGMENT' },
    { value: BagType.TWO_SEGMENT, label: 'TWO_SEGMENT' },
    { value: BagType.THREE_SEGMENT, label: 'THREE_SEGMENT' }
]; 